import React from 'react';
import styled from 'styled-components';
import { Text } from '../Typography';
import StopThumbnail from '../../Router/pages/TourEditPage/TourSideNav/StopThumbnail';

import { FormattedStopType, SearchType } from './types';
import StopRowActions from './StopRowActions';

const textSearchPlaces: (
  searchText: string,
) => Promise<google.maps.places.PlaceResult[]> = (() => {
  let placeService: google.maps.places.PlacesService;
  return async (searchText: string) => {
    // initialize placeService only when needed, otherwise it might cause problems (ENG-6457)
    if (!placeService)
      placeService = new google.maps.places.PlacesService(
        document.createElement('div'),
      );

    const location = await new Promise<google.maps.LatLng>(resolve => {
      navigator.geolocation.getCurrentPosition(
        position => {
          resolve(
            new google.maps.LatLng(
              position.coords.latitude,
              position.coords.longitude,
            ),
          );
        },
        () => resolve(undefined),
      );
    });

    return new Promise<google.maps.places.PlaceResult[]>((resolve, reject) => {
      placeService.textSearch(
        {
          query: searchText,
          ...(location && { location }),
        },
        (result, status) => {
          if (status === 'OK' || status === 'ZERO_RESULTS') resolve(result);
          reject(status);
        },
      );
    });
  };
})();

const getStopSearch: (
  setActiveRow: React.Dispatch<React.SetStateAction<null | number>>,
) => SearchType<FormattedStopType> = setActiveRow => ({
  columns: [
    {
      title: '',
      key: 'thumbnail',
      className: 'stop-thumbnail-column',
      render: () => (
        <div>
          <StopThumbnail />
        </div>
      ),
    },

    {
      title: '',
      dataIndex: 'stop',
      key: 'stop',
      className: 'stop-address-column',
      render: ({ locationName, address }) => (
        <StyledTourItemContainer>
          {locationName?.length >= 0 && <Text block>{locationName}</Text>}
          {address?.length >= 0 && <Text block>{address}</Text>}
        </StyledTourItemContainer>
      ),
    },

    {
      title: '',
      dataIndex: 'stop',
      key: 'button',
      className: 'action-buttons-column',
      render: ({ latitude, longitude, googlePlaceId, index }) => (
        <StopRowActions
          latitude={latitude}
          longitude={longitude}
          googlePlaceId={googlePlaceId}
          index={index}
        />
      ),
    },
  ],
  search: async (debouncedQuery, forceBlur, { auth }) => {
    const rawResult = await textSearchPlaces(debouncedQuery);
    return rawResult.map((searchResult, index) => ({
      key: searchResult.place_id,
      stop: {
        locationName: searchResult.name,
        address: searchResult.formatted_address,
        latitude: searchResult.geometry.location.lat().toString(),
        longitude: searchResult.geometry.location.lng().toString(),
        googlePlaceId: searchResult.place_id,
        index,
      },
    }));
  },
  onConfirm: async ({ stop }, { forceBlur, addStopToTour }) => {
    if (addStopToTour) {
      forceBlur();
      addStopToTour({
        address: stop.address,
        locationName: stop.locationName,
        coordinates: [parseFloat(stop.longitude), parseFloat(stop.latitude)],
      });
    }
  },
  createOnRow:
    ({ updateState, forceBlur, addStopToTour }) =>
    ({ stop }, index) => ({
      onClick: async () => {
        if (addStopToTour) {
          forceBlur();
          addStopToTour({
            address: stop.address,
            locationName: stop.locationName,
            coordinates: [
              parseFloat(stop.longitude),
              parseFloat(stop.latitude),
            ],
          });
        }
      },
      onMouseMove: () => setActiveRow(index),
      onMouseLeave: () => setActiveRow(null),
    }),
});

export default getStopSearch;

const StyledTourItemContainer = styled.span`
  height: 45px;

  & ${Text}:first-child {
    font-weight: 500;
    line-height: 22px;
    font-size: 14px;
  }

  & ${Text} {
    font-weight: normal;
    line-height: 20px;
    font-size: 12px;
  }
`;
